import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import Modal from "../../../components/agent/Modal";
import { sections as importedSections } from "../../../data/sections";
import useItineraryLimits from "../../../hooks/useItineraryLimits";
import ModernQuestionnaireSection from "../../../components/agent/ModernQuestionnaireSection";
import "../../../styles/pages/QuestionnaireV2.css";
import RidesRecapModal from "../../../components/agent/RidesRecapModal";
import RestaurantsRecapModal from "../../../components/agent/RestaurantsRecapModal";
import ShowsRecapModal from "../../../components/agent/ShowsRecapModal";
import { rideOptionsByPark } from "../../../data/rideOptionsByPark";
import { showOptionsByPark } from "../../../data/showOptionsByPark";
import { restaurantOptionsByPark } from "../../../data/diningOptionsByPark";
import { useToast } from "../../../context/ToastContext";
import LoadingSpinner from "../../../components/agent/UX/LoadingSpinner";
import OptimizedButton from "../../../components/agent/UX/OptimizedButton";
import useErrorHandler from "../../../hooks/useErrorHandler";
import useOptimizedFetch from "../../../hooks/useOptimizedFetch";
import {
  validateRideSelection,
  validateShowSelection,
  validateRestaurantSelection,
} from "../../../utils/validation";
import { FormDataManager } from "../../../utils/dataPersistence";
import CanvaStyleFireworks from "../../../components/agent/CanvaStyleFireworks";
import {
  validateCurrentSection,
  checkParkDaysWarning,
  shouldShowRecap,
  findFirstInvalidField,
  createQuestionnaireSubmit,
  calculateQualityScore,
} from "../../../utils/questionnaireEngine";
import QuestionnaireV2Layout from "./v2/QuestionnaireV2Layout";
import SectionHeader from "./v2/SectionHeader";
import QuoteStrip from "./v2/QuoteStrip";
import InlineErrorBanner from "./v2/InlineErrorBanner";
import FastTrackInterstitial from "./v2/FastTrackInterstitial";
import QuestionnaireModeChoice from "./v2/QuestionnaireModeChoice";
import FastTrackTipBanner from "./v2/FastTrackTipBanner";
import {
  FAST_TRACK_SECTION_IDS,
  getFastTrackSections,
  getPersonalizeMoreSections,
  isFastTrackSection,
} from "./v2/fastTrackConfig";
import { questionnaireV2Schema } from "../../../data/questionnaireV2Schema";
import { OPT } from "../../../data/questionnaireOptionValues";
import {
  evaluateVisibility,
  detectConflicts,
  normalizePayload,
} from "../../../utils/schemaRules";
import {
  PARK_KEYS,
  getEmptyRidesBuckets,
  getEmptyShowsBuckets,
  getEmptyRestaurantsBuckets,
  normalizeBucket,
  getParkDisplayName,
} from "../../../utils/parkKeys";
import {
  canonicalizeRidesBucket,
  canonicalizeShowsBucket,
  canonicalizeRestaurantsBucket,
  labelForRideId,
  labelForShowId,
  labelForRestaurantId,
  resolveRideIdToLabel,
  resolveShowIdToLabel,
  resolveRestaurantIdToLabel,
  looksLikeCanonicalId,
} from "../../../utils/canonicalize";

// Fallback sections
const fallbackSections = [
  {
    id: "basicInformation",
    title: "Basic Information",
    fields: [],
  },
];

/** ParkPro inspirational quotes (step-aware; same set as old questionnaire) */
const DISNEY_QUOTES = [
  "All our dreams can come true, if we have the courage to pursue them. – Walt Disney",
  "Ohana means family. Family means no one gets left behind or forgotten. – Lilo & Stitch",
  "You're braver than you believe, stronger than you seem, and smarter than you think. – Winnie the Pooh",
  "Adventure is out there! – Up",
  "The most fantastic, magical things can happen, and it all starts with a wish. – Pinocchio",
  "To infinity and beyond! – Toy Story",
  "Laughter is timeless, imagination has no age, dreams are forever. – Walt Disney",
  "You're never too old to be young. – Snow White and the Seven Dwarfs",
  "You've got a friend in me. – Toy Story",
  "Hakuna Matata! – The Lion King",
  "When you wish upon a star, your dreams come true. – Pinocchio",
  "First think, second believe, third dream, and finally dare. – Walt Disney",
];

const sections = importedSections || fallbackSections;

// UUID validation
const isValidUUID = (str) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

const QuestionnaireV2 = () => {
  const { tripId: tripIdParam } = useParams();
  // Safe fallback for dev routes without :tripId (e.g. /dev/questionnaire-v2) — avoid localStorage keys with "undefined"
  const tripId = tripIdParam != null && tripIdParam !== "" ? tripIdParam : null;
  const storageTripId = tripId || "dev-v2-no-trip";
  const { checkCanCreate } = useItineraryLimits();

  // Viewport setup
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(meta);
    } else {
      viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    }
    return () => {
      if (viewport) {
        viewport.content = 'width=device-width, initial-scale=1.0';
      }
    };
  }, []);

  const toastContext = useToast();
  const toast = toastContext.toast;
  const lastToastSectionRef = useRef(null);
  const { executeWithErrorHandling } = useErrorHandler();
  const { fetchWithOptimization } = useOptimizedFetch();

  // Storage keys (trip-based; use storageTripId so dev routes never get "undefined" in key)
  const STORAGE_KEY = `parkpro-form-${storageTripId}`;
  const SUBMITTED_KEY = `parkpro-form-SUBMITTED-${storageTripId}`;

  const formManager = new FormDataManager(STORAGE_KEY, SUBMITTED_KEY);
  
  // Fast Track mode storage key
  const TRACK_MODE_KEY = `${STORAGE_KEY}_trackMode`;
  const HAS_CHOSEN_MODE_KEY = `${STORAGE_KEY}_hasChosenMode`;
  const FAST_TRACK_COMPLETE_KEY = `${STORAGE_KEY}_fastTrackComplete`;

  // Cleanup old keys (trip-based only)
  useEffect(() => {
    if (tripId) {
      FormDataManager.cleanupOldKeys(tripId);
    }
  }, [tripId]);

  // Initial form data - matches exact structure; strategy fields are strings (single-choice)
  const getInitialFormData = () => {
    const emptyForm = {
      meta: { intakeMode: "standard" },
      basicInformation: { fullName: "", email: "", phoneNumber: "", startDate: "", endDate: "" },
      groupInfo: { adults: 0, numAdultMales: "", numAdultFemales: "", kids: 0, numBoys: "", numGirls: "", agesOfBoys: [], agesOfGirls: [] },
      parkTicketAccess: { ticketType: "", hasParkHopper: "", specialEvents: "" },
      parkVisitPlans: { parks: [], mkDays: "", hsDays: "", epcotDays: "", akDays: "", tlDays: "", bbDays: "", includeRestDays: "", restDayCount: "", restDayPlacement: "", restDayStyle: "", finalParkPreference: "" },
      accommodation: { transportation: "", stayingOnProperty: "", resort: "", otherHotel: "" }, // resort: string (resort name) per ResortSelector
      attractionRideStrategy: {
        lightningLane: "",
        rideStrategy: "",
        mustRides: [],
        mustRidesByPark: getEmptyRidesBuckets(),
        niceRidesByPark: getEmptyRidesBuckets(),
      },
      showAllRestaurants: false,
      showFireworkStrategy: {
        showStrategy: "",
        mustSeeShows: [],
        mustSeeShowsByPark: getEmptyShowsBuckets(),
        niceShowsByPark: getEmptyShowsBuckets(),
        fantasmicDiningPackageInterest: "",
        fantasmicDiningPackageRestaurants: [],
      },
      diningPreferences: {
        diningPlanInterest: "",
        restaurantStrategy: "",
        mustTryRestaurants: [],
        mustTryRestaurantsByPark: getEmptyRestaurantsBuckets(),
        niceRestaurantsByPark: getEmptyRestaurantsBuckets(),
        diningTime: [],
        allergies: [],
      },
      budget: { budget: "", budgetLabel: "", budgetMin: "", budgetMax: "" },
      timeEnergy: { pacing: "", parkStartTime: [], breakStyle: "", leaveParkMiddayWhere: "" },
      specialConsiderations: { celebration: "", celebrationDetails: "", celebrationDate: "", celebrationOtherDetails: "", hasDisability: "", disabilities: "", additionalInfo: "", additionalInfoDetails: "" },
      characterMeetGreets: { interestedInCharacterMeetGreets: "", mustMeetCharacters: [], mustMeetCharactersOther: "" },
      specialtyExperiences: { enchantedExperiences: "", mustExperience: [] },
    };
    const loaded = typeof window !== 'undefined' ? formManager.loadData() : null;
    if (!loaded) return emptyForm;
    // Ensure meta.intakeMode exists on load (persist + rehydrate with safe default; sync from trackMode if legacy)
    const savedTrack = typeof window !== 'undefined' ? localStorage.getItem(TRACK_MODE_KEY) : null;
    // Rehydrate intakeMode — "fast_track" is the canonical value; "fast" is legacy (map to "fast_track")
    const rawMode = loaded.meta?.intakeMode;
    const intakeMode = (rawMode === "fast_track" || rawMode === "fast")
      ? "fast_track"
      : savedTrack === "full"
        ? "standard"
        : (rawMode === "standard" ? "standard" : (savedTrack === "fast" ? "fast_track" : "standard"));
    const normalize = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;
      const out = { ...obj };
      if (Array.isArray(out.rideStrategy)) out.rideStrategy = out.rideStrategy[0] ?? "";
      if (Array.isArray(out.showStrategy)) out.showStrategy = out.showStrategy[0] ?? "";
      if (Array.isArray(out.restaurantStrategy)) out.restaurantStrategy = out.restaurantStrategy[0] ?? "";
      return out;
    };
    const normalized = {
      ...loaded,
      meta: { ...(loaded.meta || {}), intakeMode },
      parkVisitPlans: { ...emptyForm.parkVisitPlans, ...loaded.parkVisitPlans },
      characterMeetGreets: { ...emptyForm.characterMeetGreets, ...loaded.characterMeetGreets },
      specialConsiderations: { ...emptyForm.specialConsiderations, ...loaded.specialConsiderations },
      timeEnergy: (() => {
        const te = { ...emptyForm.timeEnergy, ...loaded.timeEnergy };
        const parkStartTime = te.parkStartTime;
        const parkStartTimeArray = Array.isArray(parkStartTime)
          ? parkStartTime
          : parkStartTime != null && parkStartTime !== ""
            ? [parkStartTime]
            : [];
        return {
          pacing: te.pacing ?? "",
          parkStartTime: parkStartTimeArray,
          breakStyle: te.breakStyle ?? "",
          leaveParkMiddayWhere: te.leaveParkMiddayWhere ?? "",
        };
      })(),
      attractionRideStrategy: (() => {
        const ar = loaded.attractionRideStrategy || emptyForm.attractionRideStrategy;
        const mustNorm = normalizeBucket(ar.mustRidesByPark);
        const niceNorm = normalizeBucket(ar.niceRidesByPark);
        return normalize({
          ...ar,
          mustRidesByPark: canonicalizeRidesBucket(mustNorm),
          niceRidesByPark: canonicalizeRidesBucket(niceNorm),
        });
      })(),
      showFireworkStrategy: (() => {
        const sf = { ...emptyForm.showFireworkStrategy, ...loaded.showFireworkStrategy };
        const mustNorm = normalizeBucket(sf.mustSeeShowsByPark);
        const niceNorm = normalizeBucket(sf.niceShowsByPark);
        return {
          ...sf,
          mustSeeShowsByPark: canonicalizeShowsBucket(mustNorm),
          niceShowsByPark: canonicalizeShowsBucket(niceNorm),
        };
      })(),
      diningPreferences: (() => {
        const dp = loaded.diningPreferences || emptyForm.diningPreferences;
        const mustNorm = normalizeBucket(dp.mustTryRestaurantsByPark);
        const niceNorm = normalizeBucket(dp.niceRestaurantsByPark);
        return normalize({
          ...dp,
          mustTryRestaurantsByPark: canonicalizeRestaurantsBucket(mustNorm),
          niceRestaurantsByPark: canonicalizeRestaurantsBucket(niceNorm),
        });
      })(),
    };
    return normalized;
  };

  const [formData, setFormData] = useState(getInitialFormData);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [error, setError] = useState("");
  const [inlineError, setInlineError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [delayedQuote, setDelayedQuote] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [recapType, setRecapType] = useState("rides");
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const [showParkHopperInfo, setShowParkHopperInfo] = useState(false);
  const [showGeneralWarning, setShowGeneralWarning] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [submissionStartTime, setSubmissionStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  // Fast Track mode state
  const [trackMode, setTrackMode] = useState(() => {
    if (typeof window === 'undefined') return 'fast';
    const saved = localStorage.getItem(TRACK_MODE_KEY);
    return saved === 'full' ? 'full' : 'fast';
  });
  const [showFastTrackInterstitial, setShowFastTrackInterstitial] = useState(false);
  
  // Pre-questionnaire mode choice: show choice page until user picks Fast Track or Full
  const [hasChosenMode, setHasChosenMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(HAS_CHOSEN_MODE_KEY) === 'true';
  });

  // Persist trackMode and hasChosenMode to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined' && TRACK_MODE_KEY) {
      localStorage.setItem(TRACK_MODE_KEY, trackMode);
    }
  }, [trackMode, TRACK_MODE_KEY]);
  useEffect(() => {
    if (typeof window !== 'undefined' && HAS_CHOSEN_MODE_KEY) {
      localStorage.setItem(HAS_CHOSEN_MODE_KEY, hasChosenMode ? 'true' : 'false');
    }
  }, [hasChosenMode, HAS_CHOSEN_MODE_KEY]);

  // Handlers for mode choice page (sync meta.intakeMode for schema-driven visibility)
  const handleChooseFastTrack = useCallback(() => {
    setTrackMode('fast');
    setHasChosenMode(true);
    setFormData((prev) => ({ ...prev, meta: { ...(prev.meta || {}), intakeMode: "fast_track" } }));
  }, []);
  const handleChooseFull = useCallback(() => {
    setTrackMode('full');
    setHasChosenMode(true);
    setFormData((prev) => ({ ...prev, meta: { ...(prev.meta || {}), intakeMode: "standard" } }));
  }, []);
  const handleSwitchToFull = useCallback(() => {
    setTrackMode('full');
    setFormData((prev) => ({ ...prev, meta: { ...(prev.meta || {}), intakeMode: "standard" } }));
    toast.info('Switched to full questionnaire', { duration: 2000 });
  }, [toast]);

  // Auto-save with debounce
  const debouncedAutoSave = useCallback(
    (() => {
      let timeoutId;
      let isSavingInProgress = false;
      return (data) => {
        clearTimeout(timeoutId);
        // Only start a new save if one isn't already in progress
        if (!isSavingInProgress) {
          timeoutId = setTimeout(() => {
            isSavingInProgress = true;
            setIsSaving(true);
            try {
              formManager.saveData(data);
              setLastSaved(new Date());
            } catch (error) {
              console.warn('Auto-save failed:', error);
            } finally {
              setIsSaving(false);
              isSavingInProgress = false;
            }
          }, 1000);
        }
      };
    })(),
    [formManager]
  );

  // Save on form data change
  useEffect(() => {
    debouncedAutoSave(formData);
  }, [formData, debouncedAutoSave]);

  // Check if submitted
  useEffect(() => {
    if (formManager.isSubmitted()) {
      setIsSuccess(true);
      setModalOpen(true);
      setModalMessage("success");
    }
  }, []);

  // Park Completionist events
  useEffect(() => {
    const handleParkCompletionistStart = (event) => {
      setIsAutoSelecting(true);
    };

    const handleParkCompletionistComplete = (event) => {
      setIsAutoSelecting(false);
      if (showRecap && recapType === "attractionRideStrategy") {
        setTimeout(() => {
          setShowRecap(false);
          setTimeout(() => setShowRecap(true), 200);
        }, 100);
      }
    };

    const handleParkCompletionistAutoNext = (event) => {
      if (event.detail.sectionId === 'attractionRideStrategy') {
        setRecapType('attractionRideStrategy');
        setShowRecap(true);
      }
    };

    window.addEventListener('parkCompletionistStart', handleParkCompletionistStart);
    window.addEventListener('parkCompletionistComplete', handleParkCompletionistComplete);
    window.addEventListener('parkCompletionistAutoNext', handleParkCompletionistAutoNext);

    return () => {
      window.removeEventListener('parkCompletionistStart', handleParkCompletionistStart);
      window.removeEventListener('parkCompletionistComplete', handleParkCompletionistComplete);
      window.removeEventListener('parkCompletionistAutoNext', handleParkCompletionistAutoNext);
    };
  }, [showRecap, recapType]);

  // Countdown effect
  useEffect(() => {
    if (submissionStartTime && loading) {
      const updateCountdown = () => {
        const elapsed = Date.now() - submissionStartTime;
        const remaining = Math.max(0, 30000 - elapsed);
        setTimeRemaining(remaining);
        if (remaining > 0) {
          setTimeout(updateCountdown, 1000);
        }
      };
      updateCountdown();
    } else {
      setTimeRemaining(null);
    }
  }, [submissionStartTime, loading]);

  // Schema-driven visibility (progressive disclosure)
  const visibility = useMemo(
    () => evaluateVisibility(questionnaireV2Schema, formData),
    [formData]
  );
  const visibleFieldIdsBySection = visibility.visibleFieldIdsBySection || {};

  // Schema-driven conflicts (non-blocking; show banner only)
  const conflicts = useMemo(
    () => detectConflicts(questionnaireV2Schema, formData),
    [formData]
  );

  // Filter sections based on track mode
  const currentSections = useMemo(() => {
    if (!sections || !Array.isArray(sections) || sections.length === 0) return [];
    if (trackMode === 'fast') {
      return getFastTrackSections(sections);
    }
    return sections; // Full mode shows all sections
  }, [sections, trackMode]);

  // Current section - always use the full sections array, but filter navigation
  const currentSection = useMemo(() => {
    if (!sections || sections.length === 0) return null;
    if (sectionIndex >= 0 && sectionIndex < sections.length) {
      return sections[sectionIndex];
    }
    return sections[0] || null;
  }, [sectionIndex, sections]);

  // Step-aware inspirational quote (ParkPro brand)
  const currentQuote = useMemo(() => {
    const idx = trackMode === "fast"
      ? Math.max(0, currentSections.findIndex((s) => s.id === currentSection?.id))
      : sectionIndex;
    return DISNEY_QUOTES[idx % DISNEY_QUOTES.length];
  }, [trackMode, currentSections, currentSection?.id, sectionIndex]);

  // Ensure sectionIndex stays within bounds when switching modes
  useEffect(() => {
    if (trackMode === 'fast' && currentSection && !isFastTrackSection(currentSection.id)) {
      // If we're in fast mode but on a non-fast section, go to first fast section
      const firstFastIndex = sections.findIndex(s => isFastTrackSection(s.id));
      if (firstFastIndex !== -1) {
        setSectionIndex(firstFastIndex);
      }
    }
  }, [trackMode, currentSection, sections]);
  
  // Check if Fast Track is complete (only visible fields in Fast Track sections block)
  const fastTrackComplete = useMemo(() => {
    if (trackMode === 'full') return false; // Not applicable in full mode
    const fastTrackSections = getFastTrackSections(sections);
    return fastTrackSections.every(section => {
      const visibleIds = visibleFieldIdsBySection[section.id] ?? section.fields?.map(f => f.id) ?? [];
      const validation = validateCurrentSection(section, formData, { visibleFieldIds: visibleIds });
      return validation.isValid;
    });
  }, [formData, sections, trackMode, visibleFieldIdsBySection]);

  // Quality score
  const qualityScore = useMemo(() => calculateQualityScore(formData, currentSections), [formData, currentSections]);

  // Scroll to field helper: prefer the actual field container (ModernFieldRenderer), then error banner
  const scrollToField = useCallback((fieldId, sectionId) => {
    setTimeout(() => {
      const fieldContainer = document.querySelector(`[data-questionnaire-field-id="${fieldId}"]`);
      const fieldElement = fieldContainer || document.querySelector(`[data-field-id="${fieldId}"], [name="${fieldId}"]`);
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldElement.focus?.();
        const focusable = fieldElement.querySelector?.('input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusable && typeof focusable.focus === 'function') focusable.focus();
        fieldElement.classList.add('field-error');
        setTimeout(() => fieldElement.classList.remove('field-error'), 3000);
      }
    }, 100);
  }, []);

  // Update nested form data
  const updateNestedFormData = useCallback((currentData, fieldId, newValue) => {
    const fieldToSection = {
      fullName: "basicInformation", email: "basicInformation", phoneNumber: "basicInformation",
      startDate: "basicInformation", endDate: "basicInformation",
      adults: "groupInfo", numAdultMales: "groupInfo", numAdultFemales: "groupInfo",
      kids: "groupInfo", numBoys: "groupInfo", numGirls: "groupInfo",
      agesOfBoys: "groupInfo", agesOfGirls: "groupInfo",
      ticketType: "parkTicketAccess", hasParkHopper: "parkTicketAccess", specialEvents: "parkTicketAccess",
      parks: "parkVisitPlans", mkDays: "parkVisitPlans", hsDays: "parkVisitPlans",
      epcotDays: "parkVisitPlans", akDays: "parkVisitPlans", tlDays: "parkVisitPlans",
      bbDays: "parkVisitPlans", includeRestDays: "parkVisitPlans", restDayCount: "parkVisitPlans",
      restDayPlacement: "parkVisitPlans", restDayStyle: "parkVisitPlans", finalParkPreference: "parkVisitPlans",
      transportation: "accommodation", stayingOnProperty: "accommodation",
      resort: "accommodation", otherHotel: "accommodation",
      lightningLane: "attractionRideStrategy", rideStrategy: "attractionRideStrategy", mustRides: "attractionRideStrategy",
      showStrategy: "showFireworkStrategy", mustSeeShows: "showFireworkStrategy",
      fantasmicDiningPackageInterest: "showFireworkStrategy", fantasmicDiningPackageRestaurants: "showFireworkStrategy",
      interestedInCharacterMeetGreets: "characterMeetGreets", mustMeetCharacters: "characterMeetGreets", mustMeetCharactersOther: "characterMeetGreets",
      diningPlanInterest: "diningPreferences", restaurantStrategy: "diningPreferences",
      mustTryRestaurants: "diningPreferences", diningTime: "diningPreferences", allergies: "diningPreferences",
      parkStartTime: "timeEnergy", pacing: "timeEnergy", breakStyle: "timeEnergy", leaveParkMiddayWhere: "timeEnergy",
      celebration: "specialConsiderations", celebrationDetails: "specialConsiderations",
      celebrationDate: "specialConsiderations", celebrationOtherDetails: "specialConsiderations",
      hasDisability: "specialConsiderations", disabilities: "specialConsiderations",
      additionalInfo: "specialConsiderations", additionalInfoDetails: "specialConsiderations",
      enchantedExperiences: "specialtyExperiences", mustExperience: "specialtyExperiences",
    };

    const section = fieldToSection[fieldId];
    if (section) {
      return {
        ...currentData,
        [section]: {
          ...currentData[section],
          [fieldId]: newValue,
        },
      };
    }
    return { ...currentData, [fieldId]: newValue };
  }, []);

  const handleChange = useCallback((fieldId, newValue) => {
    setFormData((prevData) => updateNestedFormData(prevData, fieldId, newValue));
    setError("");
    setInlineError(null);
  }, [updateNestedFormData]);

  const handleMultiSelect = useCallback((fieldId, selectedItems) => {
    let validationResult = { isValid: true };
    if (fieldId.includes('mustRides')) {
      validationResult = validateRideSelection(selectedItems, formData);
    } else if (fieldId.includes('mustShows')) {
      const isNotInterested = formData.showFireworkStrategy?.showStrategy?.includes(OPT.NOT_INTERESTED);
      validationResult = validateShowSelection(selectedItems, isNotInterested);
    } else if (fieldId.includes('mustRestaurants')) {
      validationResult = validateRestaurantSelection(selectedItems, formData);
    }

    if (!validationResult.isValid) {
      toast.error(validationResult.message, { duration: 3000 });
      setError(validationResult.message);
      return;
    }

    setFormData((prevData) => updateNestedFormData(prevData, fieldId, selectedItems));
    setError("");
    setInlineError(null);
  }, [formData, updateNestedFormData, toast]);

  const handleBudgetSelect = useCallback((budgetLabel, budgetMin, budgetMax) => {
    setFormData((prevData) => ({
      ...prevData,
      budget: {
        ...prevData.budget,
        budgetLabel,
        budgetMin,
        budgetMax,
      },
    }));
    setError("");
    setInlineError(null);
  }, []);

  const handleRidesByParkChange = useCallback(({ mustRidesByPark, niceRidesByPark }) => {
    setFormData((prev) => ({
      ...prev,
      attractionRideStrategy: {
        ...prev.attractionRideStrategy,
        mustRidesByPark: mustRidesByPark ?? prev.attractionRideStrategy?.mustRidesByPark,
        niceRidesByPark: niceRidesByPark ?? prev.attractionRideStrategy?.niceRidesByPark,
      },
    }));
    setError("");
    setInlineError(null);
  }, []);

  const handleShowsByParkChange = useCallback(({ mustSeeShowsByPark, niceShowsByPark }) => {
    setFormData((prev) => ({
      ...prev,
      showFireworkStrategy: {
        ...prev.showFireworkStrategy,
        mustSeeShowsByPark: mustSeeShowsByPark ?? prev.showFireworkStrategy?.mustSeeShowsByPark,
        niceShowsByPark: niceShowsByPark ?? prev.showFireworkStrategy?.niceShowsByPark,
      },
    }));
    setError("");
    setInlineError(null);
  }, []);

  const handleRestaurantsByParkChange = useCallback(({ mustTryRestaurantsByPark, niceRestaurantsByPark }) => {
    setFormData((prev) => ({
      ...prev,
      diningPreferences: {
        ...prev.diningPreferences,
        mustTryRestaurantsByPark: mustTryRestaurantsByPark ?? prev.diningPreferences?.mustTryRestaurantsByPark,
        niceRestaurantsByPark: niceRestaurantsByPark ?? prev.diningPreferences?.niceRestaurantsByPark,
      },
    }));
    setError("");
    setInlineError(null);
  }, []);

  // Group by park for recap modals (legacy flat-array path; items resolved to labels)
  const groupByPark = useCallback((items, selectedParks, optionsByPark) => {
    const result = {};
    if (!Array.isArray(items)) items = [];
    if (!Array.isArray(selectedParks)) selectedParks = [];
    if (!optionsByPark || typeof optionsByPark !== "object") optionsByPark = {};

    selectedParks.forEach((park) => {
      const parkItems = optionsByPark[park] || [];
      const selectedInPark = parkItems
        .filter((opt) => opt && opt.name && items.includes(opt.name))
        .map((opt) => opt.name);
      if (selectedInPark.length > 0) result[park] = selectedInPark;
    });
    return result;
  }, []);

  // Build recap selections from buckets (must + nice) with canonical IDs resolved to labels
  const selectionsByPark = useMemo(() => {
    if (!formData) return {};

    const parkKeyToDisplay = (key) =>
      key === "unscoped" ? "Other" : getParkDisplayName(key);

    const restaurantKeyToDisplay = (key) =>
      key === "unscoped" ? "Disney Springs" : getParkDisplayName(key);

    if (recapType === "attractionRideStrategy") {
      const must = formData.attractionRideStrategy?.mustRidesByPark || {};
      const nice = formData.attractionRideStrategy?.niceRidesByPark || {};
      const result = {};
      for (const key of [...PARK_KEYS, "unscoped"]) {
        const mustArr = Array.isArray(must[key]) ? must[key] : [];
        const niceArr = Array.isArray(nice[key]) ? nice[key] : [];
        if (mustArr.length === 0 && niceArr.length === 0) continue;
        const labels = [
          ...mustArr.map((id) => labelForRideId(key, id)),
          ...niceArr.map((id) => labelForRideId(key, id)),
        ].filter(Boolean);
        const displayName = parkKeyToDisplay(key);
        if (labels.length > 0) result[displayName] = [...new Set(labels)];
      }
      if (Object.keys(result).length > 0) return result;
      // Fallback: legacy flat array, resolve any canonical ids to labels
      const flat = formData.attractionRideStrategy?.mustRides || [];
      const resolved = flat.map((item) =>
        looksLikeCanonicalId(item) ? resolveRideIdToLabel(item) : item
      );
      return groupByPark(resolved, formData.parkVisitPlans?.parks || [], rideOptionsByPark);
    }

    if (recapType === "showFireworkStrategy") {
      const must = formData.showFireworkStrategy?.mustSeeShowsByPark || {};
      const nice = formData.showFireworkStrategy?.niceShowsByPark || {};
      const result = {};
      for (const key of [...PARK_KEYS, "unscoped"]) {
        const mustArr = Array.isArray(must[key]) ? must[key] : [];
        const niceArr = Array.isArray(nice[key]) ? nice[key] : [];
        if (mustArr.length === 0 && niceArr.length === 0) continue;
        const labels = [
          ...mustArr.map((id) => labelForShowId(key, id)),
          ...niceArr.map((id) => labelForShowId(key, id)),
        ].filter(Boolean);
        const displayName = parkKeyToDisplay(key);
        if (labels.length > 0) result[displayName] = [...new Set(labels)];
      }
      if (Object.keys(result).length > 0) return result;
      const flat = formData.showFireworkStrategy?.mustSeeShows || [];
      const resolved = flat.map((item) =>
        looksLikeCanonicalId(item) ? resolveShowIdToLabel(item) : item
      );
      return groupByPark(resolved, formData.parkVisitPlans?.parks || [], showOptionsByPark);
    }

    if (recapType === "diningPreferences") {
      const must = formData.diningPreferences?.mustTryRestaurantsByPark || {};
      const nice = formData.diningPreferences?.niceRestaurantsByPark || {};
      const result = {};
      for (const key of [...PARK_KEYS, "unscoped"]) {
        const mustArr = Array.isArray(must[key]) ? must[key] : [];
        const niceArr = Array.isArray(nice[key]) ? nice[key] : [];
        if (mustArr.length === 0 && niceArr.length === 0) continue;
        const labels = [
          ...mustArr.map((id) => labelForRestaurantId(key, id)),
          ...niceArr.map((id) => labelForRestaurantId(key, id)),
        ].filter(Boolean);
        const displayName = restaurantKeyToDisplay(key);
        if (labels.length > 0) result[displayName] = [...new Set(labels)];
      }
      if (Object.keys(result).length > 0) return result;
      const flat = formData.diningPreferences?.mustTryRestaurants || [];
      const resolved = flat.map((item) =>
        looksLikeCanonicalId(item) ? resolveRestaurantIdToLabel(item) : item
      );
      return groupByPark(resolved, formData.parkVisitPlans?.parks || [], restaurantOptionsByPark);
    }

    return {};
  }, [formData, recapType, groupByPark]);

  // Handle Next with error scroll (only visible fields block navigation)
  const handleNext = useCallback(() => {
    if (!currentSection) return;

    const visibleIds = visibleFieldIdsBySection[currentSection.id] ?? currentSection.fields?.map((f) => f.id) ?? [];
    const sectionValidation = validateCurrentSection(currentSection, formData, { visibleFieldIds: visibleIds });
    if (!sectionValidation.isValid) {
      toast.error(sectionValidation.message, { duration: 3000 });
      setError(sectionValidation.message);
      setInlineError({
        message: sectionValidation.message,
        fieldId: sectionValidation.invalidFieldId,
        sectionId: currentSection.id,
      });
      scrollToField(sectionValidation.invalidFieldId, currentSection.id);
      return;
    }

    // Park Hopper warning check
    if (currentSection.id === "parkVisitPlans") {
      const warning = checkParkDaysWarning(formData);
      if (warning?.shouldWarn) {
        const warningShown = localStorage.getItem("parkpro-shown-park-days-warning");
        if (!warningShown) {
          localStorage.setItem("parkpro-shown-park-days-warning", "true");
          if (warning.allowExtraParkDays) {
            setShowParkHopperInfo(true);
          } else {
            setShowGeneralWarning(true);
          }
          return;
        }
      }
    }

    // Recap modals
    if (shouldShowRecap(currentSection.id)) {
      if (isAutoSelecting) {
        setTimeout(() => {
          setRecapType(currentSection.id);
          setShowRecap(true);
        }, 100);
      } else {
        setRecapType(currentSection.id);
        setShowRecap(true);
      }
      return;
    }

    // Check if we're at the last Fast Track section and Fast Track is complete
    if (trackMode === 'fast') {
      const currentFastIndex = currentSections.findIndex(s => s.id === currentSection.id);
      const isLastFastTrackSection = currentFastIndex === currentSections.length - 1;
      
      if (isLastFastTrackSection && fastTrackComplete) {
        // Show Fast Track interstitial instead of advancing
        setShowFastTrackInterstitial(true);
        return;
      }
    }

    // Find next section based on track mode
    if (trackMode === 'fast') {
      // In fast mode, find next fast track section
      const currentFastIndex = currentSections.findIndex(s => s.id === currentSection.id);
      if (currentFastIndex !== -1 && currentFastIndex < currentSections.length - 1) {
        const nextFastSection = currentSections[currentFastIndex + 1];
        const nextFullIndex = sections.findIndex(s => s.id === nextFastSection.id);
        if (nextFullIndex !== -1) {
          setSectionIndex(nextFullIndex);
        }
      }
    } else {
      // In full mode, just increment
      setSectionIndex((prev) => Math.min(prev + 1, sections.length - 1));
    }
    setInlineError(null);

    if (currentSection?.id && lastToastSectionRef.current !== currentSection.id) {
      lastToastSectionRef.current = currentSection.id;
      toast.success(`${currentSection.title} completed!`, { duration: 2000 });
    }
  }, [currentSection, formData, visibleFieldIdsBySection, currentSections, sections, trackMode, fastTrackComplete, sectionIndex, isAutoSelecting, toast, scrollToField]);

  const handlePrevious = useCallback(() => {
    if (trackMode === 'fast') {
      // In fast mode, find previous fast track section
      const currentFastIndex = currentSections.findIndex(s => s.id === currentSection.id);
      if (currentFastIndex > 0) {
        const prevFastSection = currentSections[currentFastIndex - 1];
        const prevFullIndex = sections.findIndex(s => s.id === prevFastSection.id);
        if (prevFullIndex !== -1) {
          setSectionIndex(prevFullIndex);
          setInlineError(null);
        }
      }
    } else {
      // In full mode, just decrement
      if (sectionIndex > 0) {
        setSectionIndex(sectionIndex - 1);
        setInlineError(null);
      }
    }
  }, [sectionIndex, trackMode, currentSection, currentSections, sections]);

  // Fast Track handlers
  const handlePersonalizeMore = useCallback(() => {
    setTrackMode('full');
    setShowFastTrackInterstitial(false);
    // Find the index of the first personalize-more section in the full sections array
    const personalizeMoreSections = getPersonalizeMoreSections(sections);
    if (personalizeMoreSections.length > 0) {
      const firstPersonalizeSection = personalizeMoreSections[0];
      const fullIndex = sections.findIndex(s => s.id === firstPersonalizeSection.id);
      if (fullIndex !== -1) {
        setSectionIndex(fullIndex);
      } else {
        // Fallback: just continue to next section after Fast Track
        const lastFastTrackSection = getFastTrackSections(sections)[getFastTrackSections(sections).length - 1];
        const lastFastTrackIndex = sections.findIndex(s => s.id === lastFastTrackSection.id);
        if (lastFastTrackIndex !== -1 && lastFastTrackIndex < sections.length - 1) {
          setSectionIndex(lastFastTrackIndex + 1);
        }
      }
    }
    toast.info('Continuing to personalization questions...', { duration: 2000 });
  }, [sections, toast]);

  // Create submit function (trip-based); validation uses current flow (Fast Track or full) and visible fields only
  const rateLimitedSubmit = useMemo(
    () => createQuestionnaireSubmit({
      formData,
      tripId,
      isValidUUID,
      formManager,
      executeWithErrorHandling,
      fetchWithOptimization,
      setModalMessage,
      setDelayedQuote,
      setError,
      setIsSuccess,
      setSubmissionStartTime,
      setTimeRemaining,
      setLoading,
      toast,
      normalizePayload: (data) => normalizePayload(questionnaireV2Schema, data),
      sectionsForValidation: currentSections,
      visibleFieldIdsBySection,
    }),
    [formData, tripId, currentSections, visibleFieldIdsBySection, formManager, executeWithErrorHandling, fetchWithOptimization, toast]
  );

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);

      // Find first invalid field in current flow only (Fast Track or full); hidden fields do not block
      const firstInvalid = findFirstInvalidField(currentSections, formData, visibleFieldIdsBySection);
      if (firstInvalid) {
        const fullIndex = sections.findIndex(s => s.id === firstInvalid.sectionId);
        if (fullIndex !== -1) setSectionIndex(fullIndex);
        setInlineError({
          message: firstInvalid.message,
          fieldId: firstInvalid.fieldId,
          sectionId: firstInvalid.sectionId,
        });
        setTimeout(() => {
          scrollToField(firstInvalid.fieldId, firstInvalid.sectionId);
        }, 100);
        setError(firstInvalid.message);
        setLoading(false);
        return;
      }

      // Pre-submit itinerary limit check (UX); backend enforces on submit
      const canCreate = await checkCanCreate();
      if (!canCreate) {
        const limitMessage = "You've reached your itinerary limit for this period. Please upgrade your plan or try again later.";
        setError(limitMessage);
        setModalMessage("Itinerary limit reached. Please upgrade your plan or purchase additional itineraries.");
        toast.error(limitMessage, { duration: 6000 });
        setLoading(false);
        return;
      }

      await rateLimitedSubmit();
    } catch (err) {
      if (err.message.includes("Please wait")) {
        setError(err.message);
        setModalMessage("Rate limit exceeded. Please try again later.");
        toast.error(err.message, { duration: 5000 });
      } else {
        toast.error(err.message || "Something went wrong. Please try again.", { duration: 5000 });
      }
    } finally {
      setLoading(false);
    }
  }, [currentSections, formData, visibleFieldIdsBySection, sections, tripId, checkCanCreate, rateLimitedSubmit, scrollToField, toast]);

  const handleGenerateNow = useCallback(async () => {
    // Validate current flow only (Fast Track sections when in fast mode); hidden fields do not block
    const firstInvalid = findFirstInvalidField(currentSections, formData, visibleFieldIdsBySection);
    if (firstInvalid) {
      // Switch to full mode and navigate to the invalid section (map to full sections index)
      setTrackMode('full');
      setShowFastTrackInterstitial(false);
      const fullIndex = sections.findIndex(s => s.id === firstInvalid.sectionId);
      if (fullIndex !== -1) setSectionIndex(fullIndex);
      setInlineError({
        message: firstInvalid.message,
        fieldId: firstInvalid.fieldId,
        sectionId: firstInvalid.sectionId,
      });
      setTimeout(() => {
        scrollToField(firstInvalid.fieldId, firstInvalid.sectionId);
      }, 100);
      toast.error('Please complete all required fields before generating.', { duration: 4000 });
      return;
    }

    // All required fields are complete, proceed to submit
    setShowFastTrackInterstitial(false);
    await handleSubmit();
  }, [formData, currentSections, sections, visibleFieldIdsBySection, scrollToField, toast, handleSubmit]);

  // Check if Generate Now is available (current flow only; hidden fields do not block)
  const canGenerateNow = useMemo(() => {
    const firstInvalid = findFirstInvalidField(currentSections, formData, visibleFieldIdsBySection);
    return !firstInvalid;
  }, [formData, currentSections, visibleFieldIdsBySection]);

  const generateNowReason = useMemo(() => {
    if (canGenerateNow) return null;
    const firstInvalid = findFirstInvalidField(currentSections, formData, visibleFieldIdsBySection);
    return firstInvalid ? `Please complete: ${firstInvalid.message}` : 'Please complete all required fields.';
  }, [canGenerateNow, formData, currentSections, visibleFieldIdsBySection]);

  const handleRecapConfirm = () => {
    setShowRecap(false);
    const current = sections[sectionIndex];
    if (!current) return;
    if (trackMode === 'fast') {
      const currentFastIndex = currentSections.findIndex(s => s.id === current.id);
      if (currentFastIndex !== -1 && currentFastIndex < currentSections.length - 1) {
        const nextFastSection = currentSections[currentFastIndex + 1];
        const nextFullIndex = sections.findIndex(s => s.id === nextFastSection.id);
        if (nextFullIndex !== -1) setSectionIndex(nextFullIndex);
      }
    } else {
      setSectionIndex((prev) => Math.min(prev + 1, sections.length - 1));
    }
  };

  const handleRecapBack = () => {
    setShowRecap(false);
  };

  // Pre-questionnaire: show mode choice page
  if (!hasChosenMode) {
    return (
      <div className="min-h-screen questionnaire-v2-shell">
        <QuestionnaireModeChoice
          onChooseFastTrack={handleChooseFastTrack}
          onChooseFull={handleChooseFull}
          isAgentForm={false}
          agentInfo={null}
          agencyInfo={null}
          publicInfoError=""
        />
      </div>
    );
  }

  // Success screen
  if (isSuccess) {
    return (
      <div className="flex fixed inset-0 justify-center items-center backdrop-blur-sm z-9999 bg-black/75">
        <div className="relative p-8 mx-4 w-full max-w-md text-center q2-success-card z-10000">
          <div className="flex flex-col items-center">
            <div className="flex justify-center items-center mb-6 w-24 h-24 rounded-full q2-success-icon">
              <svg className="w-14 h-14 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mb-4 text-4xl font-bold q2-success-title">Success!</h2>
            <p className="mb-4 text-lg q2-success-text">
              Your trip info has been submitted successfully!
            </p>
            <p className="mb-6 text-sm q2-success-subtext">
              Your travel agent will be in touch soon to start planning your magical adventure.
            </p>
            <button
              onClick={() => {
                try {
                  window.close();
                  setTimeout(() => {
                    if (!window.closed) {
                      window.location.href = 'about:blank';
                    }
                  }, 200);
                } catch (error) {
                  window.location.href = 'about:blank';
                }
              }}
              className="px-6 py-3 mt-6 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
            >
              Close Page
            </button>
          </div>
        </div>
        <CanvaStyleFireworks />
      </div>
    );
  }

  return (
    <div className="min-h-screen questionnaire-v2-shell">
      <QuestionnaireV2Layout
        formData={formData}
        qualityScore={qualityScore}
        isSaving={isSaving}
        lastSaved={lastSaved}
        sidebarContent={
          trackMode === 'fast' ? (
            <FastTrackTipBanner
              sectionId={currentSection?.id}
              onSwitchToFull={handleSwitchToFull}
            />
          ) : null
        }
      >
        <SectionHeader
          section={currentSection}
          sectionIndex={sectionIndex}
          totalSections={currentSections.length}
          trackMode={trackMode}
          isFastTrack={trackMode === 'fast'}
        />

        <QuoteStrip quote={currentQuote} />

        {conflicts.length > 0 && (
          <div className="flex gap-3 items-start px-4 py-3 mb-4 rounded-lg q2-panel-warning" role="status">
            <span className="shrink-0 mt-0.5" aria-hidden>⚠</span>
            <div>
              <p className="text-sm font-medium">Please review your answers.</p>
              <ul className="mt-1 text-sm list-disc list-inside">
                {conflicts.slice(0, 2).map((c, i) => (
                  <li key={c.code || i}>{c.message}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {inlineError && inlineError.sectionId === currentSection?.id && (
          <InlineErrorBanner
            message={inlineError.message}
            fieldId={inlineError.fieldId}
            onDismiss={() => setInlineError(null)}
          />
        )}

        {currentSection ? (
          <ModernQuestionnaireSection
            section={currentSection}
            formData={formData}
            onFieldChange={handleChange}
            onMultiSelect={handleMultiSelect}
            onBudgetSelect={handleBudgetSelect}
            onRidesByParkChange={handleRidesByParkChange}
            onShowsByParkChange={handleShowsByParkChange}
            onRestaurantsByParkChange={handleRestaurantsByParkChange}
            intakeMode={formData.meta?.intakeMode || "standard"}
            validationErrors={inlineError ? { [inlineError.fieldId]: inlineError.message } : {}}
            isActive={true}
            isCompleted={false}
            isCollapsible={false}
            sectionIndex={sectionIndex}
            totalSections={currentSections.length}
            visibleFieldIdsBySection={visibleFieldIdsBySection}
          />
        ) : (
          <div className="py-8 text-center">
            <LoadingSpinner size="lg" variant="primary" text="Loading questionnaire sections..." />
          </div>
        )}

        <div className="flex flex-col gap-3 justify-between mt-12 sm:flex-row">
          <div className="flex gap-3">
            {sectionIndex > 0 && (
              <OptimizedButton onClick={handlePrevious} variant="secondary">
                Previous
              </OptimizedButton>
            )}
            {trackMode === 'fast' && (
              <OptimizedButton
                onClick={handleSwitchToFull}
                variant="secondary"
                className="text-xs"
              >
                Switch to Full Questionnaire
              </OptimizedButton>
            )}
          </div>

          {(() => {
            // Check if we're at the last section based on track mode
            const isLastSection = trackMode === 'fast' 
              ? currentSections.findIndex(s => s.id === currentSection.id) === currentSections.length - 1
              : sectionIndex === sections.length - 1;
            
            return !isLastSection ? (
              <OptimizedButton onClick={handleNext} variant="primary">
                Next
              </OptimizedButton>
            ) : (
              <OptimizedButton onClick={handleSubmit} disabled={loading} variant="primary">
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" variant="white" className="mr-2" />
                    Submitting...
                  </>
                ) : (
                  "Submit Trip Info"
                )}
              </OptimizedButton>
            );
          })()}
        </div>
      </QuestionnaireV2Layout>

      {/* Modals */}
      <Modal
        isOpen={modalOpen}
        message={modalMessage}
        onClose={() => setModalOpen(false)}
        isError={!!error}
        disneyQuote={delayedQuote}
      />

      {recapType === "attractionRideStrategy" && (
        <RidesRecapModal
          visible={showRecap}
          onClose={handleRecapBack}
          onConfirm={handleRecapConfirm}
          title="Review Your Ride Selections"
          selectionsByPark={selectionsByPark}
          formData={formData}
        />
      )}

      {recapType === "diningPreferences" && (
        <RestaurantsRecapModal
          visible={showRecap}
          onClose={handleRecapBack}
          onConfirm={handleRecapConfirm}
          title="Review Your Restaurant Selections"
          selectionsByPark={selectionsByPark}
          formData={formData}
        />
      )}

      {recapType === "showFireworkStrategy" && (
        <ShowsRecapModal
          visible={showRecap}
          onClose={handleRecapBack}
          onConfirm={handleRecapConfirm}
          title="Review Your Show Selections"
          selectionsByPark={selectionsByPark}
          formData={formData}
        />
      )}

      {showParkHopperInfo && (
        <div className="flex fixed inset-0 justify-center items-center p-4 backdrop-blur-sm z-100 bg-black/50">
          <div className="p-6 w-full max-w-md rounded-xl q2-modal-panel">
            <div className="space-y-4 text-center">
              <h2 className="text-lg font-bold q2-modal-title">Just a heads up!</h2>
              <p className="text-sm q2-modal-text">
                It looks like you've selected more park days than your total trip length. If you're planning to park hop or have an Annual Pass, that's totally fine — just continue ahead.
              </p>
              <div className="flex gap-3 justify-center mt-4">
                <OptimizedButton onClick={() => { setShowParkHopperInfo(false); setSectionIndex(sectionIndex + 1); }} variant="primary">
                  Sounds Good!
                </OptimizedButton>
                <OptimizedButton onClick={() => setShowParkHopperInfo(false)} variant="secondary">
                  Go Back
                </OptimizedButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {showGeneralWarning && (
        <div className="flex fixed inset-0 justify-center items-center p-4 backdrop-blur-sm z-100 bg-black/50">
          <div className="p-6 w-full max-w-md rounded-xl q2-modal-panel">
            <div className="text-center">
              <h2 className="text-lg font-bold q2-modal-title">Park Days Exceed Trip Length</h2>
              <p className="mt-2 text-sm q2-modal-text">
                You've selected more park days than your trip length. You can either review your selections or enable Park Hopping.
              </p>
            </div>
            <div className="flex gap-3 justify-center mt-6">
              <OptimizedButton onClick={() => { setShowGeneralWarning(false); setSectionIndex(sectionIndex + 1); }} variant="primary">
                Review Selections
              </OptimizedButton>
              <OptimizedButton
                onClick={() => {
                  setShowGeneralWarning(false);
                  localStorage.removeItem("parkpro-shown-park-days-warning");
                  const parkTicketSectionIndex = sections.findIndex(s => s.id === "parkTicketAccess");
                  if (parkTicketSectionIndex !== -1) setSectionIndex(parkTicketSectionIndex);
                }}
                variant="secondary"
              >
                Enable Park Hopping
              </OptimizedButton>
            </div>
          </div>
        </div>
      )}

      {/* Fast Track Interstitial */}
      {showFastTrackInterstitial && (
        <FastTrackInterstitial
          qualityScore={qualityScore}
          onPersonalizeMore={handlePersonalizeMore}
          onGenerateNow={handleGenerateNow}
          canGenerateNow={canGenerateNow}
          generateNowReason={generateNowReason}
        />
      )}
    </div>
  );
};

export default QuestionnaireV2;